import { useEffect, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import AxiosInstance from "service/axiosInstance";

function formatDateTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleString();
}

export default function AuditLogDrawer({
  isOpen,
  onClose,
  transactionId,
  isDeleted,
}) {
  const [logs, setLogs] = useState([]);
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen || !transactionId) return;

    setLoading(true);
    setError("");
    setLogs([]);

    AxiosInstance({
      method: "GET",
      url: `api/v1/admin/transaction/${transactionId}/audit-logs`,
    })
      .then((res) => {
        // AxiosInstance interceptor returns response.data
        // API shape: { data: { transaction: {...}, logs: [...] }, meta: {...} }
        const logs = res?.data?.logs ?? res?.logs ?? [];
        const tx = res?.data?.transaction ?? res?.transaction ?? null;
        setLogs(logs);
        setCurrentTransaction(tx);
      })
      .catch((err) => {
        setError(
          err?.message || err?.error || "Failed to load audit logs."
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isOpen, transactionId]);

  return (
    <Drawer anchor="right" open={isOpen} onClose={onClose}>
      <Box sx={{ width: 420, p: 3, display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              Audit Log
            </Typography>
            {isDeleted && (
              <Chip
                label="Deleted"
                color="error"
                size="small"
                data-testid="deleted-chip"
              />
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Body */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error">{error}</Alert>
        )}

        {!loading && !error && logs.length === 0 && (
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            No audit log entries found.
          </Typography>
        )}

        {!loading && !error && (logs.length > 0 || currentTransaction) && (
          <Timeline sx={{ p: 0, m: 0 }}>
            {/* ── Current state (top of timeline) ── */}
            {currentTransaction && (
              <TimelineItem
                data-testid="audit-log-current"
                sx={{ "&::before": { display: "none" } }}
              >
                <TimelineSeparator>
                  <TimelineDot color="success">
                    <HistoryIcon sx={{ fontSize: 14 }} />
                  </TimelineDot>
                  {logs.length > 0 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent sx={{ pb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Current State
                    </Typography>
                    <Chip
                      label="current"
                      color="success"
                      size="small"
                      sx={{ height: 18, fontSize: "0.675rem" }}
                    />
                  </Box>
                  <Box sx={{ mt: 0.5 }}>
                    {currentTransaction.amount != null && (
                      <Typography variant="body2">Amount: {currentTransaction.amount}</Typography>
                    )}
                    {currentTransaction.type && (
                      <Typography variant="body2">Type: {currentTransaction.type}</Typography>
                    )}
                    {currentTransaction.mode && (
                      <Typography variant="body2">Mode: {currentTransaction.mode}</Typography>
                    )}
                    {currentTransaction.subCategory && (
                      <Typography variant="body2">Category: {currentTransaction.subCategory}</Typography>
                    )}
                    {currentTransaction.description && (
                      <Typography variant="body2">Sub-category: {currentTransaction.description}</Typography>
                    )}
                    {currentTransaction.transactionDate && (
                      <Typography variant="body2">
                        Transaction Date: {formatDateTime(currentTransaction.transactionDate)}
                      </Typography>
                    )}
                    {currentTransaction.interest != null && (
                      <Typography variant="body2">
                        Interest: {currentTransaction.interest ? "Yes" : "No"}
                      </Typography>
                    )}
                  </Box>
                </TimelineContent>
              </TimelineItem>
            )}
            {logs.map((entry, index) => (
              <TimelineItem
                key={entry.id ?? index}
                data-testid="audit-log-entry"
                sx={{ "&::before": { display: "none" } }}
              >
                <TimelineSeparator>
                  <TimelineDot
                    color={
                      entry.eventType === "DELETED"
                        ? "error"
                        : entry.eventType === "RESTORED"
                        ? "success"
                        : "info"
                    }
                  >
                    {entry.eventType === "DELETED" ? (
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    ) : entry.eventType === "RESTORED" ? (
                      <RestoreIcon sx={{ fontSize: 14 }} />
                    ) : (
                      <EditIcon sx={{ fontSize: 14 }} />
                    )}
                  </TimelineDot>
                  {index < logs.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent sx={{ pb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {entry.editorName || "Unknown"}
                    </Typography>
                    <Chip
                      label={entry.eventType?.toLowerCase() || "edited"}
                      color={
                        entry.eventType === "DELETED"
                          ? "error"
                          : entry.eventType === "RESTORED"
                          ? "success"
                          : "info"
                      }
                      size="small"
                      sx={{ height: 18, fontSize: "0.675rem", textTransform: "capitalize" }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(entry.createdAt)}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {entry.amount != null && (
                      <Typography variant="body2">
                        Amount: {entry.amount}
                      </Typography>
                    )}
                    {entry.type && (
                      <Typography variant="body2">
                        Type: {entry.type}
                      </Typography>
                    )}
                    {entry.mode && (
                      <Typography variant="body2">
                        Mode: {entry.mode}
                      </Typography>
                    )}
                    {entry.subCategory && (
                      <Typography variant="body2">
                        Category: {entry.subCategory}
                      </Typography>
                    )}
                    {entry.description && (
                      <Typography variant="body2">
                        Sub-category: {entry.description}
                      </Typography>
                    )}
                    {entry.transactionDate && (
                      <Typography variant="body2">
                        Transaction Date: {formatDateTime(entry.transactionDate)}
                      </Typography>
                    )}
                    {entry.interest != null && (
                      <Typography variant="body2">
                        Interest: {entry.interest ? "Yes" : "No"}
                      </Typography>
                    )}
                  </Box>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        )}
      </Box>
    </Drawer>
  );
}
